import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { APP_NAME } from "@/config";
import { useFlags } from "@/lib/flags";
import { Page, PageHeader, SectionTitle, Label11, StatusBadge, Tag, Segmented, Loading, Empty, usd, int, plural, shortDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import ConfirmDestructive from "@/components/ConfirmDestructive";
import { DeletionBadge } from "@/components/AccountDeletion";
import { CheckCircle2, Loader2, Pencil, Plus, TriangleAlert, XCircle } from "lucide-react";

// design_handoff_crewbench/screens/Admin Store.dc.html: credits and payments (FEATURE_STORE) and workspace servers
// (FEATURE_PROVISIONING). The design's company, product and amount per payment come with the backend later: a
// webhook delivery records its type, object and result.
const PROVIDERS = [["anthropic", "Anthropic"], ["openai", "OpenAI"]];
const PROVIDER_NAMES = Object.fromEntries(PROVIDERS);
const when = (iso) => (iso ? shortDate(iso, true) : "—");
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—");
const signed = (n, digits = 2) => `${n < 0 ? "−" : ""}${usd(Math.abs(n), digits)}`;
const limitText = (n) => (n == null ? "no limit" : usd(n));
// order_created → "Order created"
const words = (s) => { const t = (s || "").replace(/_/g, " "); return t.charAt(0).toUpperCase() + t.slice(1); };
const ERR = "text-sm text-gr-warning";

// The handoff's tables: rows on a grid over the card surface, a hairline between them, the header in the label style
function Row({ cols, min, className, as: Tag = "div", children, ...props }) {
  return (
    <Tag className={cn("grid gap-3 items-center px-4 py-2 border-t", className)} style={{ gridTemplateColumns: cols, minWidth: min }} {...props}>
      {children}
    </Tag>
  );
}

function Grid({ cols, min, head, className, children, ...props }) {
  return (
    <div className={cn("bg-card rounded-[10px] overflow-x-auto text-sm tabular-nums", className)} {...props}>
      <Row cols={cols} min={min} className="py-2.5 border-t-0 items-end text-xs uppercase tracking-[0.09em] text-muted-foreground">{head}</Row>
      {children}
    </div>
  );
}

function Panel({ title, className, children, ...props }) {
  return (
    <section className={cn("bg-card rounded-[10px] px-5 py-[18px] flex flex-col gap-2.5", className)} {...props}>
      {title && <SectionTitle as="h3">{title}</SectionTitle>}
      {children}
    </section>
  );
}

const Note = ({ className, children }) => <p className={cn("m-0 text-sm text-muted-foreground [text-wrap:pretty]", className)}>{children}</p>;

function Field({ id, label, className, children }) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label htmlFor={id} className="text-sm font-semibold">{label}</label>
      {children}
    </div>
  );
}

function EditButton({ label, ...props }) {
  return <Button size="icon" variant="ghost" className="justify-self-end" aria-label={label} title="Edit" {...props}><Pencil /></Button>;
}

const onSale = (on) => <StatusBadge status={on ? "on sale" : "off"} tone={on ? "info" : "muted"} />;

// ---------------- Companies ----------------
const LEDGER_KIND = { topup: "Top-up", plan_grant: "Plan grant", seat_grant: "Seat bonus", plan_upgrade: "Plan upgrade", plan_reset: "Plan reset", refund: "Refund",
  chargeback: "Chargeback", adjustment: "Adjustment", expiry: "Expiry", suspension: "Suspended", suspension_lifted: "Suspension lifted",
  debt_paid: "Debt paid from a refund", debt_written_off: "Debt written off" };
const BUCKET = { plan: "Plan", topup: "Top-up" };
const SOURCE = { payment: "Lemon Squeezy", admin: "An admin", system: APP_NAME };
const LEDGER_COLS = "100px 120px 100px 64px minmax(0,1.3fr) minmax(0,1fr) 110px";

function CompanyDetail({ cid, onClose }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["store-company", cid], queryFn: async () => (await api.get(`/admin/store/companies/${cid}`)).data });
  const [adjust, setAdjust] = useState({ amount_usd: "", reason: "" });
  const [limitsMode, setLimitsMode] = useState("default");
  const [limits, setLimits] = useState({ daily_spend_usd: "", topup_max_usd: "" });
  const [suspendReason, setSuspendReason] = useState("");
  const [seatLimits, setSeatLimits] = useState({ employee: "", independent: "" });
  useEffect(() => { const l = data?.seat_limits; setSeatLimits({ employee: l?.employee ?? "", independent: l?.independent ?? "" }); }, [data]);
  const saveSeats = useMutation({  // one company's own seats and slots (backend/seats.py); empty = the plan's
    mutationFn: async () => api.put(`/admin/store/companies/${cid}/seats`,
      Object.fromEntries(Object.entries(seatLimits).map(([k, v]) => [k, v === "" ? null : Number(v)]))),
    onSuccess: () => { refresh(); toast.success("Seats saved."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  useEffect(() => {
    const l = data?.company?.account?.limits;
    setLimitsMode(l ? "custom" : "default");
    setLimits({ daily_spend_usd: l?.daily_spend_usd ?? "", topup_max_usd: l?.topup_max_usd ?? "" });
  }, [data]);
  useEffect(() => { setAdjust({ amount_usd: "", reason: "" }); setSuspendReason(""); }, [cid]);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["store-company", cid] }); qc.invalidateQueries({ queryKey: ["store-companies"] }); };
  const doAdjust = useMutation({
    mutationFn: async () => api.post(`/admin/store/companies/${cid}/adjust`, { amount_usd: Number(adjust.amount_usd), reason: adjust.reason }),
    onSuccess: () => { refresh(); setAdjust({ amount_usd: "", reason: "" }); toast.success("Credits adjusted."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const suspension = useMutation({
    mutationFn: async (action) => api.post(`/admin/store/companies/${cid}/${action}`, { reason: suspendReason }),
    onSuccess: (_, action) => { refresh(); setSuspendReason(""); toast.success(action === "suspend" ? "Credits suspended." : "Suspension lifted."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const saveLimits = useMutation({
    mutationFn: async () => api.put(`/admin/store/companies/${cid}/limits`, {
      limits: limitsMode === "default" ? null : Object.fromEntries(Object.entries(limits).map(([k, v]) => [k, v === "" ? null : Number(v)])) }),
    onSuccess: () => { refresh(); toast.success("Limits saved."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!data) return <Loading />;
  const c = data.company;
  const a = c.account;
  const suspended = a.status === "suspended";
  const amount = Number(adjust.amount_usd);
  const canAdjust = adjust.amount_usd !== "" && !Number.isNaN(amount) && amount !== 0 && adjust.reason.trim().length >= 3;
  return (
    <section aria-label={c.name} className="bg-card rounded-[10px] p-5 flex flex-col gap-[22px]" data-testid="store-company-detail">
      <div className="flex justify-between items-start gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="font-display text-[22px] leading-[1.25] tracking-[0.02em] font-semibold">{c.name}</span>
          <span className="text-sm text-muted-foreground tabular-nums">
            Balance {usd(a.balance_usd, 4)} (plan {usd(a.plan_usd, 4)} · top-up {usd(a.topup_usd, 4)}) · today {usd(a.spend_day_usd, 4)} of {limitText(c.daily_limit_usd)}
            {suspended && " · suspended"}
          </span>
          {c.deletion?.status === "scheduled" && (
            <span className="text-sm text-gr-danger [text-wrap:pretty]" data-testid="store-company-deletion">
              Its founder is deleting the account: everything goes on {day(c.deletion.delete_after)}, unless a founder undoes
              it before then. Its credits are suspended until then; Undo lifts that.
              {c.deletion.error && ` The last try failed (${c.deletion.error}): it's tried again every hour.`}
            </span>
          )}
          {c.deleted_at && <span className="text-sm text-muted-foreground">Deleted on {day(c.deleted_at)}: only its money records and the audit log are kept.</span>}
        </div>
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </div>
      {data.seats && (
        <div className="flex flex-col gap-2.5" data-testid="store-company-seats">
          <span className="text-sm tabular-nums">Seats: {data.seats.employee.in_use} of {data.seats.employee.included} team member seats, {data.seats.independent.in_use} of {data.seats.independent.included} independent professionals · {data.seats.extra} extra</span>
          <div className="flex flex-wrap items-end gap-3">
            {[["employee", "Team member seats"], ["independent", "Independent professionals"]].map(([k, label]) => (
              <Field key={k} id={`seats-${k}`} label={`${label} (empty: the plan's)`}>
                <Input id={`seats-${k}`} type="number" min="0" className="w-36 tabular-nums" value={seatLimits[k]}
                  onChange={(e) => setSeatLimits({ ...seatLimits, [k]: e.target.value })} />
              </Field>
            ))}
            <Button variant="outline" disabled={saveSeats.isPending} onClick={() => saveSeats.mutate()} data-testid="store-company-seats-save">Save seats</Button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-x-8 gap-y-6">
        <div className="flex-[1_1_300px] flex flex-col gap-2">
          <SectionTitle as="h3">Grant or deduct credits</SectionTitle>
          <Note>On the top-up balance. A negative amount deducts. The reason goes into the ledger and the audit log.</Note>
          <Input type="number" step="any" placeholder="Amount in $, e.g. 25 or -5" value={adjust.amount_usd} className="tabular-nums"
            aria-label="Amount" onChange={(e) => setAdjust({ ...adjust, amount_usd: e.target.value })} data-testid="adjust-amount" />
          <Input placeholder="Reason (required), e.g. pilot credits" value={adjust.reason} aria-label="Reason"
            onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })} data-testid="adjust-reason" />
          <div><Button onClick={() => doAdjust.mutate()} disabled={!canAdjust || doAdjust.isPending} data-testid="adjust-submit">
            {amount < 0 ? "Deduct" : "Grant"}</Button></div>
        </div>
        <div className="flex-[1_1_300px] flex flex-col gap-2">
          <SectionTitle as="h3">Limits</SectionTitle>
          <Note>Now: {limitText(c.daily_limit_usd)} per day, {limitText(c.topup_limit_usd)} per top-up{c.new_company && " (new company)"}.</Note>
          <RadioGroup value={limitsMode} onValueChange={setLimitsMode} aria-label="Limits" className="gap-2">
            {[["default", "The new-company defaults"], ["custom", "This company's own (empty: no limit)"]].map(([v, l]) => (
              <div key={v} className="flex items-center gap-2.5">
                <RadioGroupItem value={v} id={`limits-${v}`} data-testid={`limits-${v}`} />
                <label htmlFor={`limits-${v}`} className="cursor-pointer">{l}</label>
              </div>
            ))}
          </RadioGroup>
          {limitsMode === "custom" && (
            <div className="flex gap-2">
              <Input type="number" min="0" step="any" placeholder="$ per day" value={limits.daily_spend_usd} aria-label="Daily spend limit" className="tabular-nums"
                onChange={(e) => setLimits({ ...limits, daily_spend_usd: e.target.value })} data-testid="limits-daily" />
              <Input type="number" min="0" step="any" placeholder="$ per top-up" value={limits.topup_max_usd} aria-label="Top-up limit" className="tabular-nums"
                onChange={(e) => setLimits({ ...limits, topup_max_usd: e.target.value })} data-testid="limits-topup" />
            </div>
          )}
          <div><Button variant="outline" onClick={() => saveLimits.mutate()} disabled={saveLimits.isPending} data-testid="limits-save">Save limits</Button></div>
        </div>
      </div>
      <div className="flex flex-col gap-2" data-testid="store-suspension">
        <SectionTitle as="h3">{suspended ? "Credits suspended" : "Suspend credits"}</SectionTitle>
        <Note>
          {suspended
            ? "New requests on credits are refused and the company can't buy. Lift it once the dispute is settled."
            : "On a dispute or chargeback (Lemon Squeezy emails you; there's no event): deduct the disputed credits above, then suspend. New requests on credits stop."}
          {" "}The reason goes into the ledger and the audit log.
        </Note>
        <div className="flex flex-wrap gap-2">
          <Input placeholder="Reason (required), e.g. dispute on order 1234" value={suspendReason} aria-label="Suspension reason" className="flex-[1_1_260px] w-auto min-w-0"
            onChange={(e) => setSuspendReason(e.target.value)} data-testid="suspend-reason" />
          <Button variant="outline" className={suspended ? undefined : "border-gr-danger text-gr-danger"}
            onClick={() => suspension.mutate(suspended ? "lift" : "suspend")}
            disabled={suspendReason.trim().length < 3 || suspension.isPending} data-testid="suspend-submit">
            {suspended ? "Lift suspension" : "Suspend credits"}
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <SectionTitle as="h3">Ledger</SectionTitle>
        {data.ledger.length === 0 ? <Note>No entries yet. Usage on credits is in the company's usage events.</Note> : (
          <Grid cols={LEDGER_COLS} min={780} className="bg-transparent border rounded-lg" data-testid="store-ledger"
            head={<><span>When</span><span>Kind</span><span className="text-right">Amount</span><span>Bucket</span><span>Source</span><span>Reason</span><span className="text-right">Balance after</span></>}>
            {data.ledger.map((e) => (
              <Row key={e.id} cols={LEDGER_COLS} min={780} className="items-start">
                <span className="text-muted-foreground">{when(e.created_at)}</span>
                <span>{LEDGER_KIND[e.kind] || words(e.kind)}</span>
                <span className={cn("text-right", e.amount_usd < 0 && "text-gr-danger")}>{e.amount_usd ? `${e.amount_usd > 0 ? "+" : ""}${signed(e.amount_usd, 4)}` : "—"}</span>
                <span>{BUCKET[e.bucket] || e.bucket}</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">{SOURCE[e.source] || e.source}{e.source === "payment" && e.source_ref && <span className="block font-mono text-xs text-muted-foreground">{e.source_ref}</span>}</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">{e.reason || "—"}</span>
                <span className="text-right">{e.balance_after ? usd(e.balance_after.plan_usd + e.balance_after.topup_usd, 4) : "—"}</span>
              </Row>
            ))}
          </Grid>
        )}
      </div>
    </section>
  );
}

const COMPANY_COLS = "minmax(0,1.5fr) minmax(0,1.3fr) minmax(0,1.3fr) 110px 140px 48px";

function CompaniesTab() {
  const { data } = useQuery({ queryKey: ["store-companies"], queryFn: async () => (await api.get("/admin/store/companies")).data });
  const [open, setOpen] = useState(null);
  if (!data) return <Loading />;
  const rec = data.reconciliation;
  const mismatched = new Set(rec.mismatches.map((m) => m.company_id));
  return (
    <div className="flex flex-col gap-4" data-testid="store-companies">
      <span className="text-sm text-muted-foreground">
        Daily check of every balance against its ledger and usage: {rec.ran_at ? `last run ${when(rec.ran_at)}` : "not run yet"}
        {mismatched.size > 0 && <span className="text-gr-warning"> · {mismatched.size} to check (nothing was changed)</span>}
      </span>
      {data.companies.length === 0 ? <Empty>No companies yet.</Empty> : (
        <Grid cols={COMPANY_COLS} min={820}
          head={<><span>Company</span><span>Anthropic</span><span>OpenAI</span><span className="text-right">Balance</span><span className="text-right">Today</span><span /></>}>
          {data.companies.map((c) => (
            <Row key={c.id} as="button" type="button" cols={COMPANY_COLS} min={820} onClick={() => setOpen(open === c.id ? null : c.id)} aria-expanded={open === c.id}
              className={cn("w-full text-left py-3 hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                open === c.id && "bg-gr-hover-strong hover:bg-gr-hover-strong")} data-testid={`store-company-${c.id}`}>
              <span className="flex flex-wrap items-center gap-1.5 min-w-0">
                {mismatched.has(c.id) && <TriangleAlert className="w-3.5 h-3.5 shrink-0 text-gr-warning" aria-label="Balance to check" />}
                <span className="text-base font-semibold">{c.name}</span>
                {c.new_company && <Tag>New</Tag>}
                {c.account.status === "suspended" && <Tag tone="danger">Suspended</Tag>}
                <DeletionBadge deletion={c.deletion} deletedAt={c.deleted_at} />
              </span>
              {PROVIDERS.map(([p]) => (
                <span key={p} className="flex flex-col min-w-0">
                  <span>{c.billing[p] === "credits" ? "Credits" : "Own key"}</span>
                  {c.keys.filter((k) => k.provider === p).map((k) => (
                    <span key={k.label} className="font-mono text-xs text-muted-foreground [overflow-wrap:anywhere]">{k.label} •••• {k.last4}</span>
                  ))}
                </span>
              ))}
              <span className="text-right">{usd(c.account.balance_usd, 4)}</span>
              <span className="text-right">{usd(c.account.spend_day_usd)} / {limitText(c.daily_limit_usd)}</span>
              <span className="text-right font-semibold">Open</span>
            </Row>
          ))}
        </Grid>
      )}
      {open && <CompanyDetail cid={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ---------------- Plans and top-ups, and server sizes: edited in one form dialog ----------------
const FORMS = {
  plan: {
    path: "/admin/store/plans", saved: "Plan saved.", flag: ["enabled", "On sale"],
    empty: { name: "", display_price: "", variant_id: "", monthly_credits_usd: "", order: 0, support_text: "", server_size_id: "", enabled: false,
             seats: "", pros: "", price_usd: "", company_id: "" },
    fields: [["name", "Name", "text"], ["display_price", "Price shown (e.g. $49/month)", "text"], ["variant_id", "Lemon Squeezy variant id", "text"],
      ["monthly_credits_usd", "Credits every month ($)", "number"], ["order", "Order in lists", "number"], ["support_text", "Support level", "text"],
      // seats and limits (backend/seats.py): what it includes, its price to count extras from, and a custom plan's company
      ["seats", "Team member seats", "number"], ["pros", "Independent professionals", "number"], ["price_usd", "Monthly price ($, before tax)", "number"],
      ["company_id", "Custom plan: for one company only (its id; empty for everyone)", "text"]],
    title: (f) => (f.id ? `Edit ${f.name || "plan"}` : "Add a plan"),
  },
  topup: {
    path: "/admin/store/topups", saved: "Top-up saved.", flag: ["enabled", "On sale"],
    empty: { display_price: "", credits_usd: "", variant_id: "", order: 0, enabled: false },
    fields: [["display_price", "Price shown (e.g. $10)", "text"], ["credits_usd", "Credits ($)", "number"], ["variant_id", "Lemon Squeezy variant id", "text"],
      ["order", "Order in lists", "number"]],
    title: (f) => (f.id ? `Edit the ${usd(Number(f.credits_usd) || 0, 0)} top-up` : "Add a top-up"),
  },
  size: {
    path: "/admin/store/sizes", saved: "Server size saved.", flag: ["enabled", "Enabled"],
    empty: { label: "", hetzner_type: "", max_active_sessions: 2, monthly_cost_usd: "", order: 0, enabled: false, dedicated: false, by_hand: false },
    fields: [["label", "Label", "text"], ["hetzner_type", "Hetzner type", "text"], ["max_active_sessions", "Sessions at once", "number"],
      ["monthly_cost_usd", "Monthly cost ($)", "number"], ["order", "Order in lists", "number"]],
    title: (f) => (f.id ? `Edit ${f.label || "size"}` : "Add a server size"),
  },
};
const blank = (v) => !String(v ?? "").trim();

function formError(kind, f) {
  if (kind === "size") return blank(f.label) || (!f.by_hand && blank(f.hetzner_type)) ? "Add a label and a Hetzner type (none for a size installed by hand)." : "";
  if (kind === "plan" && blank(f.name)) return "Give the plan a name.";
  if (kind === "plan" && !f.server_size_id) return "Choose its workspace server.";
  if (f.enabled && (blank(f.variant_id) || blank(f.display_price))) return "Only items with a variant id and a price shown can go on sale.";
  return "";
}

// what the API takes: numbers as numbers, an empty optional number as null
function formBody(kind, f) {
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const { id, created_at, ...body } = f;  // eslint-disable-line no-unused-vars
  body.order = Number(body.order || 0);
  if (kind === "plan") {
    body.monthly_credits_usd = num(body.monthly_credits_usd);
    ["seats", "pros", "price_usd"].forEach((k) => { body[k] = num(body[k]); });
    body.company_id = blank(body.company_id) ? null : body.company_id.trim();
  }
  if (kind === "topup") body.credits_usd = num(body.credits_usd);
  if (kind === "size") {
    body.max_active_sessions = Number(body.max_active_sessions); body.monthly_cost_usd = num(body.monthly_cost_usd);
    body.hetzner_type = body.by_hand || blank(body.hetzner_type) ? null : body.hetzner_type.trim();
  }
  return body;
}

function useFormSave(kind, queryKeys, onDone) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (f) => {
      const { path } = FORMS[kind];
      return f.id ? api.put(`${path}/${f.id}`, formBody(kind, f)) : api.post(path, formBody(kind, f));
    },
    onSuccess: () => { queryKeys.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); onDone(); toast.success(FORMS[kind].saved); },
    onError: (e) => toast.error(apiErr(e)),
  });
}

function FormDialog({ kind, edit, setEdit, sizes, onSave, saving }) {
  const last = useRef(null);  // what the dialog showed, kept while it animates closed
  if (edit) last.current = edit;
  const f = edit || last.current;
  const form = FORMS[kind];
  const error = f ? formError(kind, f) : "";
  const set = (k, v) => setEdit({ ...edit, [k]: v });
  const [flagKey, flagLabel] = form.flag;
  return (
    <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
      <DialogContent className="gap-3.5" aria-describedby={undefined}>
        <DialogTitle>{f && form.title(f)}</DialogTitle>
        {f && (
          <>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-3">
              {form.fields.map(([k, l, t]) => (
                <Field key={k} id={`${kind}-${k}`} label={l}>
                  <Input id={`${kind}-${k}`} type={t} step="any" value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)}
                    disabled={kind === "size" && k === "hetzner_type" && !!f.by_hand} placeholder={kind === "size" && k === "hetzner_type" && f.by_hand ? "None: installed by hand" : undefined}
                    className={cn(t === "number" && "tabular-nums", (k === "variant_id" || k === "hetzner_type") && "font-mono text-[12px]")} data-testid={`${kind}-${k}`} />
                </Field>
              ))}
            </div>
            {kind === "plan" && (
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-semibold">Workspace server</span>
                <div role="radiogroup" aria-label="Workspace server" className="flex flex-wrap gap-2" data-testid="plan-server_size_id">
                  {sizes.map((z) => {
                    const on = f.server_size_id === z.id;
                    return (
                      <button key={z.id} type="button" role="radio" aria-checked={on} onClick={() => set("server_size_id", z.id)}
                        className={cn("inline-flex items-center h-8 px-3 rounded-md font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                          on ? "bg-gr-selected text-gr-on-selected" : "border border-gr-control-border hover:bg-gr-hover")}>
                        {z.label}{z.enabled ? "" : " (disabled)"}
                      </button>
                    );
                  })}
                  {sizes.length === 0 && <Note>Add a server size first.</Note>}
                </div>
              </div>
            )}
            {kind === "size" && (
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2.5 self-start cursor-pointer font-semibold">
                  <Switch checked={!!f.dedicated} onCheckedChange={(v) => set("dedicated", v)} data-testid="size-dedicated" />Dedicated vCPUs
                </label>
                <label className="flex items-center gap-2.5 self-start cursor-pointer font-semibold">
                  <Switch checked={!!f.by_hand} onCheckedChange={(v) => set("by_hand", v)} data-testid="size-by_hand" />Installed by hand
                </label>
                <Note>Dedicated vCPUs: Hetzner's CCX types, or a whole machine; plans say so. Installed by hand: a machine an
                  admin installs the runner on (a dedicated server). It's never created automatically, and its runner takes
                  the plan's session limit when it registers.</Note>
              </div>
            )}
            <label className="flex items-center gap-2.5 self-start cursor-pointer font-semibold">
              <Switch checked={!!f[flagKey]} onCheckedChange={(v) => set(flagKey, v)} data-testid={`${kind}-enabled`} />{flagLabel}
            </label>
            {error && <span className={ERR}>{error}</span>}
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
          <Button onClick={onSave} disabled={!!error || saving} data-testid={`${kind}-save`}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const PLAN_COLS = "minmax(0,1fr) 110px 90px 120px minmax(0,1.3fr) 96px 40px";
const TOPUP_COLS = "120px 110px minmax(0,1fr) 96px 40px";
const byOrder = (a, b) => (a.order || 0) - (b.order || 0);

function CatalogTab() {
  const { data } = useQuery({ queryKey: ["store-catalog-admin"], queryFn: async () => (await api.get("/admin/store/catalog")).data });
  const [plan, setPlan] = useState(null);
  const [topup, setTopup] = useState(null);
  const savePlan = useFormSave("plan", ["store-catalog-admin"], () => setPlan(null));
  const saveTopup = useFormSave("topup", ["store-catalog-admin"], () => setTopup(null));
  if (!data) return <Loading />;
  const sizeLabel = (id) => data.sizes.find((z) => z.id === id)?.label || "—";
  const add = (label, onClick, testid) => <Button onClick={onClick} data-testid={testid}><Plus />{label}</Button>;
  return (
    <div className="flex flex-col gap-5" data-testid="store-catalog">
      <Note className="max-w-[860px]">
        Create each product in Lemon Squeezy first (plans as subscriptions, top-ups as single payments), then paste its variant id
        here. Only items with a variant id and a price shown can go on sale, and the price shown must match Lemon Squeezy's.
        Founders see what's on sale in their Store and on the pricing page.
      </Note>
      <section className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between gap-3">
          <SectionTitle as="h3">Plans</SectionTitle>{add("Add plan", () => setPlan({ ...FORMS.plan.empty }), "plan-add")}
        </div>
        {data.plans.length === 0 ? <Empty>No plans yet.</Empty> : (
          <Grid cols={PLAN_COLS} min={780}
            head={<><span>Plan</span><span>Price</span><span>Variant</span><span className="text-right">Credits / month</span><span>Server</span><span>On sale</span><span /></>}>
            {data.plans.slice().sort(byOrder).map((p) => (
              <Row key={p.id} cols={PLAN_COLS} min={780} data-testid={`plan-row-${p.id}`}>
                <span className="text-base font-semibold min-w-0 [overflow-wrap:anywhere]">{p.name}</span>
                <span>{p.display_price || "—"}</span>
                <span className="font-mono text-xs">{p.variant_id || "—"}</span>
                <span className="text-right">{usd(p.monthly_credits_usd)}</span>
                <span className="min-w-0">{sizeLabel(p.server_size_id)}</span>
                <span className="flex">{onSale(p.enabled)}</span>
                <EditButton label={`Edit ${p.name}`} onClick={() => setPlan({ ...p })} data-testid={`plan-edit-${p.id}`} />
              </Row>
            ))}
          </Grid>
        )}
      </section>
      <section className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between gap-3">
          <SectionTitle as="h3">Top-ups</SectionTitle>{add("Add top-up", () => setTopup({ ...FORMS.topup.empty }), "topup-add")}
        </div>
        {data.topups.length === 0 ? <Empty>No top-ups yet.</Empty> : (
          <Grid cols={TOPUP_COLS} min={560} head={<><span>Credits</span><span>Price</span><span>Variant</span><span>On sale</span><span /></>}>
            {data.topups.slice().sort(byOrder).map((t) => (
              <Row key={t.id} cols={TOPUP_COLS} min={560} data-testid={`topup-row-${t.id}`}>
                <span className="text-base font-semibold">{usd(t.credits_usd)}</span>
                <span>{t.display_price || "—"}</span>
                <span className="font-mono text-xs">{t.variant_id || "—"}</span>
                <span className="flex">{onSale(t.enabled)}</span>
                <EditButton label={`Edit the ${usd(t.credits_usd, 0)} top-up`} onClick={() => setTopup({ ...t })} data-testid={`topup-edit-${t.id}`} />
              </Row>
            ))}
          </Grid>
        )}
      </section>
      <FormDialog kind="plan" edit={plan} setEdit={setPlan} sizes={data.sizes} saving={savePlan.isPending} onSave={() => savePlan.mutate(plan)} />
      <FormDialog kind="topup" edit={topup} setEdit={setTopup} saving={saveTopup.isPending} onSave={() => saveTopup.mutate(topup)} />
    </div>
  );
}

// ---------------- Payments: Lemon Squeezy's webhook deliveries, and those that never arrived ----------------
const EVENT_TONE = { granted: "info", applied: "info", processing: "warning", unmatched: "warning", mismatch: "danger" };
const EVENT_COLS = "110px minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) 90px 120px";
// what a payment event recorded (GET /admin/store/payments: `payment`, or null when it granted nothing)
const paidAmount = (p) => (p?.paid_cents == null ? "—" : `${usd(p.paid_cents / 100)}${p.currency && p.currency !== "USD" ? ` ${p.currency}` : ""}`);
const amountOf = (m) => (m.total == null ? "—" : `${(m.total / 100).toFixed(2)} ${m.currency || ""}`.trim());

function PaymentsTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["store-payments"], queryFn: async () => (await api.get("/admin/store/payments")).data, refetchInterval: 15000 });
  const [dismissing, setDismissing] = useState(null);
  const shown = useRef(null);  // kept while the dialog animates closed
  if (dismissing) shown.current = dismissing;
  const [reason, setReason] = useState("");
  const dismiss = useMutation({
    mutationFn: async () => api.post(`/admin/store/payments/missing/${encodeURIComponent(dismissing.id)}/dismiss`, { reason: reason.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["store-payments"] }); setDismissing(null); setReason(""); toast.success("Flag dismissed."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!data) return <Loading />;
  const m = shown.current;
  return (
    <div className="flex flex-col gap-4" data-testid="store-payments">
      <Note>Every payment Lemon Squeezy reports, newest first. Dismissing a flag records your reason in the audit log.</Note>
      <span className="text-sm text-muted-foreground" data-testid="payments-setup">
        Lemon Squeezy: {data.ready ? "API key and store set" : <span className="text-gr-warning">LEMONSQUEEZY_API_KEY or LEMONSQUEEZY_STORE_ID not set</span>}
        {" "}· webhook secret {data.webhook_secret_set ? "set" : <span className="text-gr-warning">not set</span>}
        {" "}· {data.test_mode ? <span className="text-gr-warning">test mode</span> : "live"}
      </span>
      {data.missing.length > 0 && (
        <Panel title="Flagged">
          <Note>
            Paid orders and invoices of the last 7 days whose webhook never reached us (checked every 15 minutes; Lemon Squeezy retries
            only for about 2.5 minutes). Nothing was granted for them. They leave this list once they arrive.
          </Note>
          {data.missing.map((x) => (
            <div key={x.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 pt-2.5 border-t" data-testid={`missing-${x.id}`}>
              <div className="flex-[1_1_360px] flex gap-2.5 min-w-0">
                <TriangleAlert className="w-4 h-4 shrink-0 mt-0.5 text-gr-warning" aria-hidden="true" />
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="font-semibold">A paid {x.kind} of {amountOf(x)} never reached us.</span>
                  <span className="text-sm text-muted-foreground [text-wrap:pretty]">
                    <span className="font-mono">{x.id}</span>{x.identifier && ` (${x.identifier})`} · {when(x.created_at)}. Resend it from Lemon Squeezy
                    (Settings → Webhooks). Dismiss it only if it needs nothing from us.
                  </span>
                </div>
              </div>
              <Button variant="outline" onClick={() => { setReason(""); setDismissing(x); }} data-testid={`dismiss-${x.id}`}>Dismiss</Button>
            </div>
          ))}
        </Panel>
      )}
      {data.events.length === 0 ? <Empty>No payments yet.</Empty> : (
        <Grid cols={EVENT_COLS} min={760} head={<><span>When</span><span>Event</span><span>Company</span><span>Product</span>
          <span className="text-right">Amount</span><span>Result</span></>}>
          {data.events.slice(0, 50).map((e) => (
            <Row key={e.event_id} cols={EVENT_COLS} min={760} className="py-2.5 items-start" data-testid={`payment-event-${e.object_ref || e.event_id}`}>
              <span className="text-muted-foreground">{when(e.received_at)}</span>
              <span className="flex flex-col min-w-0">
                <span>{words(e.type)}</span>
                <span className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
                  <span className="font-mono">{e.object_ref || e.event_id}</span>{e.detail && ` · ${e.detail}`}
                </span>
              </span>
              <span className="min-w-0 [overflow-wrap:anywhere]">{e.payment?.company || "—"}</span>
              <span className="min-w-0">{e.payment?.product || "—"}</span>
              <span className="text-right tabular-nums">{paidAmount(e.payment)}</span>
              <span className="flex"><StatusBadge status={e.result} tone={EVENT_TONE[e.result] || "muted"} /></span>
            </Row>
          ))}
        </Grid>
      )}
      {data.dismissed?.length > 0 && (
        <details className="group text-sm" data-testid="missing-dismissed">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            Dismissed flags ({data.dismissed.length}{data.dismissed.length === 50 ? ", the latest" : ""})</summary>
          <Grid cols="110px minmax(0,1fr) 110px minmax(0,1fr) minmax(0,1.4fr)" min={700} className="mt-2"
            head={<><span>Dismissed</span><span>What</span><span className="text-right">Amount</span><span>Reference</span><span>Reason</span></>}>
            {data.dismissed.map((x) => (
              <Row key={x.id} cols="110px minmax(0,1fr) 110px minmax(0,1fr) minmax(0,1.4fr)" min={700} className="items-start" data-testid={`dismissed-${x.id}`}>
                <span className="text-muted-foreground">{when(x.dismissed_at)}</span>
                <span>{words(x.kind)}</span>
                <span className="text-right">{amountOf(x)}</span>
                <span className="font-mono text-xs min-w-0 [overflow-wrap:anywhere]">{x.id}</span>
                <span className="text-muted-foreground min-w-0 [overflow-wrap:anywhere]">{x.dismiss_reason}</span>
              </Row>
            ))}
          </Grid>
        </details>
      )}
      <Dialog open={!!dismissing} onOpenChange={(v) => !v && setDismissing(null)}>
        <DialogContent className="gap-3.5">
          <DialogTitle>Dismiss this flag?</DialogTitle>
          <DialogDescription className="text-foreground [text-wrap:pretty]">
            {m && <><span className="font-mono">{m.id}</span> ({m.kind}, {amountOf(m)}). Dismiss it only if it needs nothing from us: another
              app's payment on the shared store, or one you've settled by hand. It stays listed under Dismissed flags and isn't flagged again. </>}
            Say why you're dismissing it. The reason goes into the audit log.
          </DialogDescription>
          <Input aria-label="Reason" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dismiss-reason" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setDismissing(null)}>Cancel</Button>
            <Button onClick={() => dismiss.mutate()} disabled={reason.trim().length < 3 || dismiss.isPending} data-testid="dismiss-confirm">Dismiss flag</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Report: the Store's money per month ----------------
const REPORT_COLUMNS = [
  ["credits_sold_usd", "Credits sold"], ["credits_refunded_usd", "Refunded"], ["credits_granted_usd", "Granted"],
  ["credits_unused_usd", "Unused"], ["usage_charged_usd", "Usage charged"], ["provider_cost_usd", "Provider cost"],
  ["usage_margin_usd", "Usage margin"],
];
const REPORT_COLS = "96px repeat(7,minmax(86px,1fr)) 80px 130px";
const monthName = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });

function ReportTab() {
  const { data } = useQuery({ queryKey: ["store-report"], queryFn: async () => (await api.get("/admin/store/report")).data });
  if (!data) return <Loading />;
  const row = (r, label, testid, total) => (
    <Row key={label} cols={REPORT_COLS} min={1100} className={cn("py-2.5", total && "border-gr-control-border font-semibold")} data-testid={testid}>
      <span>{label}</span>
      {REPORT_COLUMNS.map(([k]) => <span key={k} className={cn("text-right", k === "usage_margin_usd" && "font-semibold")}>{usd(r[k])}</span>)}
      <span className="text-right">{int(r.requests)}</span>
      <span className="text-right">
        {usd(r.server_cost_usd)}{r.servers != null && ` · ${r.servers}`}
        {r.servers_without_cost > 0 && <span className="block text-gr-warning" title="Sizes without a monthly cost aren't counted">{r.servers_without_cost} without a cost</span>}
      </span>
    </Row>
  );
  return (
    <div className="flex flex-col gap-4" data-testid="store-report">
      <Note className="max-w-[860px]">
        Per calendar month (UTC). Credits sold are what payments granted; unused are plan credits reset at a renewal and expired
        top-ups. The usage margin is usage charged on credits minus the providers' price. Servers are what the Store's Hetzner
        servers cost us, from each size's monthly cost and the days the server existed. Lemon Squeezy's fees and the taxes it
        collects are in its own reports.
      </Note>
      <Grid cols={REPORT_COLS} min={1100}
        head={<><span>Month</span>{REPORT_COLUMNS.map(([k, l]) => <span key={k} className="text-right">{l}</span>)}<span className="text-right">Requests</span><span className="text-right">Servers</span></>}>
        {data.months.map((r) => row(r, monthName(r.month), `report-${r.month}`))}
        {row({ ...data.totals, servers: null, servers_without_cost: 0 }, "Total", "report-total", true)}
      </Grid>
    </div>
  );
}

// ---------------- Servers (FEATURE_PROVISIONING) ----------------
const ORDER_TONE = { online: "info", creating: "warning", installing: "warning", ending: "warning", requested: "muted", deleted: "muted",
  failed: "danger", ready_to_delete: "danger" };
const ORDER_COLS = "minmax(0,1.1fr) minmax(0,1fr) minmax(0,1.2fr) minmax(0,1.1fr) 120px 110px 180px";

// The backup checklist of a server whose plan ended (plan 4.8): deletion only once every item passes, which the
// backend checks again.
function Checklist({ order, onBackup, backingUp, onDelete }) {
  const passed = order.checklist.every((i) => i.ok);
  const fixable = order.checklist.some((i) => !i.ok && i.key !== "sessions_locked");
  return (
    <div className="flex-[1_1_340px] flex flex-col gap-2" data-testid={`order-checklist-${order.id}`}>
      <span className="font-semibold">Before deleting {order.server_name}</span>
      {order.checklist.map((i) => (
        <div key={i.key} className="flex items-start gap-2" data-testid={`check-${i.key}-${i.ok ? "ok" : "no"}`}>
          {i.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-px text-gr-success" aria-label="Passes" />
            : <XCircle className="w-4 h-4 shrink-0 mt-px text-gr-danger" aria-label="Doesn't pass" />}
          <span><span className="font-semibold">{i.label}. </span><span className="text-muted-foreground">{i.detail}</span></span>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 pt-0.5">
        {fixable && (
          <Button size="sm" variant="outline" onClick={onBackup} disabled={backingUp} data-testid={`order-backup-${order.id}`}
            title="Archives the missing homes to R2 and pushes the branches whose last push failed (the server must be online)">
            {backingUp && <Loader2 className="animate-spin" />}Back up now
          </Button>
        )}
        {order.status === "ready_to_delete" && (
          <Button size="sm" variant="outline" className="border-gr-danger text-gr-danger" disabled={!passed} onClick={onDelete}
            title={passed ? undefined : "Every item must pass first"} data-testid={`order-delete-${order.id}`}>Delete server</Button>
        )}
        {order.status === "ending" && order.grace_until && (<>
          <span className="text-muted-foreground">It's deleted after {shortDate(order.grace_until)}, once every item passes.</span>
          <Button size="sm" variant="outline" className="border-gr-danger text-gr-danger" disabled={!passed} onClick={onDelete}
            title={passed ? "Before the end of its grace period: the founders are told" : "Every item must pass first"}
            data-testid={`order-delete-early-${order.id}`}>Delete now</Button>
        </>)}
      </div>
    </div>
  );
}

function ServersTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["store-servers"], queryFn: async () => (await api.get("/admin/store/servers")).data, refetchInterval: 5000 });
  const { data: sizes } = useQuery({ queryKey: ["store-sizes"], queryFn: async () => (await api.get("/admin/store/sizes")).data });
  const { data: companies } = useQuery({ queryKey: ["admin-companies"], queryFn: async () => (await api.get("/admin/companies")).data });
  const [form, setForm] = useState({ company_id: "", size_id: "" });
  const [open, setOpen] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["store-servers"] });
  const order = useMutation({
    mutationFn: async () => api.post("/admin/store/servers", form),
    onSuccess: () => { refresh(); setForm({ company_id: "", size_id: "" }); toast.success("Server ordered. It's created within a minute; the runner registers a few minutes later."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const retry = useMutation({
    mutationFn: async (id) => api.post(`/admin/store/servers/${id}/retry`),
    onSuccess: () => { refresh(); toast.success("Retrying. An existing server is reused, never duplicated."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const [asking, setAsking] = useState(null);  // {kind: "delete" | "end", order}: confirmed with the password
  const remove = useMutation({
    mutationFn: async ({ id, name, password }) => api.post(`/admin/store/servers/${id}/delete`, { confirm_name: name, password }),
    onSuccess: () => { setAsking(null); refresh(); toast.success("Server deleted at Hetzner."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const backup = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/store/servers/${id}/backup`)).data,
    onSuccess: (r) => {
      refresh();
      (r.failed ? toast.warning : toast.success)(`Archived ${plural(r.archived, "home")} and pushed ${plural(r.pushed, "branch", "branches")}${r.failed ? `; ${r.failed} failed` : ""}.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const end = useMutation({  // a server ordered by hand (no plan): ended like a plan's, then deleted after the checklist
    mutationFn: async ({ id, password }) => (await api.post(`/admin/store/servers/${id}/end`, { password })).data,
    onSuccess: ({ order: o }) => {
      setAsking(null);
      setOpen(o.id);
      refresh();
      toast.success(o.status === "ready_to_delete" ? "Ended. It never had a session: it can be deleted now."
        : `Ended. Its sessions are locked; it's kept until ${shortDate(o.grace_until)}.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const [resizing, setResizing] = useState(null);
  const shownResize = useRef(null);
  if (resizing) shownResize.current = resizing;
  const resized = useMutation({
    mutationFn: async (id) => api.post(`/admin/store/servers/${id}/resized`),
    onSuccess: () => { refresh(); setResizing(null); toast.success("Marked resized: the order and the runner's session limit follow."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const retryResize = useMutation({
    mutationFn: async (id) => api.post(`/admin/store/servers/${id}/resize-retry`),
    onSuccess: () => { refresh(); toast.success("It's resized automatically once none of its sessions is open."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const sizeOf = (id) => (sizes?.sizes || []).find((z) => z.id === id);
  if (!data) return <Loading />;
  const s = data.setup;
  // one per company: not those with a runner, nor those with an order that isn't deleted
  const busy = new Set(data.orders.filter((o) => o.status !== "deleted").map((o) => o.company_id));
  const free = (companies?.companies || []).filter((c) => !c.runners && !c.deleted_at && !busy.has(c.id));
  const enabled = (sizes?.sizes || []).filter((z) => z.enabled);
  const rz = shownResize.current && sizeOf(shownResize.current.resize_to);
  return (
    <div className="flex flex-col gap-4" data-testid="store-servers">
      <ConfirmDestructive open={!!asking} onOpenChange={(v) => !v && setAsking(null)}
        title={asking?.kind === "delete" ? `Delete ${asking?.order.server_name} at Hetzner?` : `End ${asking?.order.server_name}?`}
        action={asking?.kind === "delete" ? "Delete server" : "End server"}
        confirmName={asking?.kind === "delete" ? asking?.order.server_name : undefined}
        pending={remove.isPending || end.isPending}
        onConfirm={(password, name) => (asking.kind === "delete"
          ? remove.mutate({ id: asking.order.id, name, password }) : end.mutate({ id: asking.order.id, password }))}>
        {asking?.kind === "delete"
          ? <p>The server and everything on it are deleted at Hetzner, and its runner is retired.{asking?.order.status === "ending"
            && ` Its grace period (until ${shortDate(asking.order.grace_until)}) ends now: the founders can't come back to this server, and they're told.`}</p>
          : <p>For a server you ordered by hand, or one whose plan was paid in test mode. Its sessions are locked and backed up, no
            new ones start, and the founders are told. It's kept for the grace period, then deleted after its checklist passes.
            One that never had a session can be deleted right away.</p>}
      </ConfirmDestructive>
      <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]" data-testid="hetzner-setup">
        Hetzner: {s.token_set ? "token set" : <span className="text-gr-warning">HETZNER_API_TOKEN not set</span>} · {s.location} · {s.image}
        {" "}· SSH keys {s.ssh_keys.length ? s.ssh_keys.join(", ") : "none"} · firewall {s.firewall}: SSH from {s.ssh_from.length ? s.ssh_from.join(", ") : "nowhere (no inbound at all)"}
      </span>
      <Panel title="Order a server for a company">
        <Note>
          No payment needed, for example for the pilot. Only companies without a workspace server or an order are listed: one per
          company. It's created on Hetzner and installs the runner itself. A server is deleted only after its backup checklist passes.
        </Note>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {free.length === 0 ? <span>Every company has a workspace server or an order.</span> : (
            <>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="w-[220px]" aria-label="Company" data-testid="order-company"><SelectValue placeholder="Company" /></SelectTrigger>
                <SelectContent>{free.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.size_id} onValueChange={(v) => setForm({ ...form, size_id: v })}>
                <SelectTrigger className="w-[320px] max-w-full" aria-label="Size" data-testid="order-size"><SelectValue placeholder={enabled.length ? "Size" : "No size enabled yet"} /></SelectTrigger>
                <SelectContent>{enabled.filter((z) => !z.by_hand).map((z) => <SelectItem key={z.id} value={z.id}>{z.label} · {z.hetzner_type} · {plural(z.max_active_sessions, "session")}</SelectItem>)}</SelectContent>
              </Select>
            </>
          )}
          <Button onClick={() => order.mutate()} disabled={!form.company_id || !form.size_id || order.isPending} data-testid="order-submit">Order</Button>
        </div>
      </Panel>
      {data.orders.length === 0 ? <Empty>No servers ordered yet.</Empty> : (
        <Grid cols={ORDER_COLS} min={960} head={<><span>Company</span><span>Server</span><span>Type</span><span>Status</span><span>IPv4</span><span>Ordered</span><span /></>}>
          {data.orders.map((o) => {
            const expanded = open === o.id || (o.status === "ready_to_delete" && !!o.checklist);
            const toggle = () => setOpen(open === o.id ? null : o.id);
            return (
              <div key={o.id} className="border-t" style={{ minWidth: 960 }}>
                <Row cols={ORDER_COLS} className="border-t-0 py-2.5 cursor-pointer hover:bg-gr-hover" onClick={toggle} title="Show its history" data-testid={`order-row-${o.id}`}>
                  <span className="text-base min-w-0 [overflow-wrap:anywhere]">{o.company_name || o.company_id}</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); toggle(); }} aria-expanded={expanded}
                    className="justify-self-start font-mono text-left min-w-0 [overflow-wrap:anywhere] rounded-sm hover:underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring">
                    {o.server_name}</button>
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="font-mono">{o.hetzner_type}</span>
                    {o.resize ? (
                      <Tag tone="info" data-testid={`order-resize-${o.id}`} title={`Resizing automatically: ${o.resize.step}`}>Resizing → <span className="font-mono normal-case">{o.resize.type}</span></Tag>
                    ) : o.resize_to && (
                      <Tag tone={o.resize_failed ? "warning" : "muted"} data-testid={`order-resize-${o.id}`}
                        title={o.resize_failed ? "The automatic resize failed: see below" : "Resized automatically once none of its sessions is open"}>
                        Resize → <span className="font-mono normal-case">{sizeOf(o.resize_to)?.hetzner_type || o.resize_to}</span></Tag>
                    )}
                  </span>
                  <span className="flex flex-col items-start gap-0.5">
                    <StatusBadge status={o.status} label={o.status.replace(/_/g, " ")} tone={ORDER_TONE[o.status] || "muted"} />
                    {o.status === "ending" && o.grace_until && <span className="text-muted-foreground">kept until {shortDate(o.grace_until)}</span>}
                  </span>
                  <span className="font-mono">{o.ipv4 || "—"}</span>
                  <span className="text-muted-foreground">{when(o.created_at)}</span>
                  <span className="flex flex-wrap justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {o.status === "failed" && (<>
                      <Button size="sm" variant="ghost" onClick={() => retry.mutate(o.id)} disabled={retry.isPending} data-testid={`order-retry-${o.id}`}>Retry</Button>
                      <Button size="sm" variant="ghost" className="text-gr-danger" onClick={() => setAsking({ kind: "delete", order: o })} data-testid={`order-delete-${o.id}`}>Delete</Button>
                    </>)}
                    {o.resize_to && o.resize_failed && !o.resize && (<>
                      <Button size="sm" variant="ghost" onClick={() => retryResize.mutate(o.id)} disabled={retryResize.isPending} data-testid={`order-resize-retry-${o.id}`}>Retry resize</Button>
                      <Button size="sm" variant="ghost" onClick={() => setResizing(o)} data-testid={`order-resized-${o.id}`}>Mark resized</Button>
                    </>)}
                    {o.status === "online" && (
                      <Button size="sm" variant="ghost" className="text-gr-danger" onClick={() => setAsking({ kind: "end", order: o })} disabled={end.isPending}
                        title="For a server ordered by hand: a plan's server ends with its plan" data-testid={`order-end-${o.id}`}>End</Button>
                    )}
                  </span>
                </Row>
                {o.status === "failed" && o.hetzner_server_id && (
                  <p className="m-0 px-4 pb-2.5 flex items-start gap-2 text-muted-foreground [text-wrap:pretty]" data-testid={`order-failed-note-${o.id}`}>
                    <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gr-warning" aria-hidden="true" />
                    The server exists at Hetzner but its runner never registered. Retry reuses this server and doesn't run the install
                    again, so it only helps if the install did finish (for example the runner couldn't reach us for a while). Otherwise
                    delete it (typing its name), then order a new one.
                  </p>
                )}
                {o.resize_to && o.resize_failed && !o.resize && (
                  <p className="m-0 px-4 pb-2.5 flex items-start gap-2 text-muted-foreground [text-wrap:pretty]" data-testid={`order-resize-failed-${o.id}`}>
                    <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-0.5 text-gr-warning" aria-hidden="true" />
                    <span className="min-w-0 [overflow-wrap:anywhere]">The automatic resize failed: {o.resize_failed}. It runs at its old size. Retry resize once the cause
                    is fixed, or resize it by hand and mark it resized.</span>
                  </p>
                )}
                {expanded && (
                  <div className="px-4 pt-1 pb-4 flex flex-wrap gap-x-10 gap-y-4">
                    {o.checklist && <Checklist order={o} onBackup={() => backup.mutate(o.id)} backingUp={backup.isPending}
                      onDelete={() => setAsking({ kind: "delete", order: o })} />}
                    <div className="flex-[1_1_340px] flex flex-col gap-1" data-testid={`order-events-${o.id}`}>
                      <span className="font-semibold">History</span>
                      {o.events.map((e, i) => (
                        <div key={i} className="grid grid-cols-[100px_minmax(0,1fr)] gap-3">
                          <span className="text-muted-foreground">{when(e.at)}</span><span className="[overflow-wrap:anywhere]">{words(e.step)}{e.detail && `: ${e.detail}`}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </Grid>
      )}
      <Dialog open={!!resizing} onOpenChange={(v) => !v && setResizing(null)}>
        <DialogContent className="gap-3.5">
          <DialogTitle>Resize {shownResize.current?.server_name}</DialogTitle>
          <DialogDescription className="text-foreground [text-wrap:pretty]">
            The company's plan changed to the {rz?.label} ({rz?.hetzner_type}, {plural(rz?.max_active_sessions || 0, "session")} at once), and the automatic
            resize failed: {shownResize.current?.resize_failed}.
          </DialogDescription>
          <div className="flex flex-col gap-1.5 text-muted-foreground [text-wrap:pretty]">
            <span>1. Pick a time when the company has no unlocked sessions (Admin → Sessions): powering off stops what runs on it.</span>
            <span>2. In Hetzner's console, power the server off and rescale it to {rz?.hetzner_type}, keeping its disk as it is (CPU and RAM only), so it can go back down later.</span>
            <span>3. Power it on. The runner reconnects by itself.</span>
            <span>4. Mark it resized here: the order and the runner's session limit change to the new size.</span>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setResizing(null)}>Cancel</Button>
            <Button onClick={() => resized.mutate(resizing.id)} disabled={resized.isPending} data-testid="resized-confirm">Mark resized</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Server sizes (the admin catalog) ----------------
const SIZE_COLS = "minmax(0,1.4fr) 120px 140px 120px 100px 40px";

function SizesTab() {
  const { data } = useQuery({ queryKey: ["store-sizes"], queryFn: async () => (await api.get("/admin/store/sizes")).data });
  const [edit, setEdit] = useState(null);
  const save = useFormSave("size", ["store-sizes", "store-catalog-admin"], () => setEdit(null));
  if (!data) return <Loading />;
  return (
    <div className="flex flex-col gap-3" data-testid="store-sizes">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Note className="flex-[1_1_360px]">What plans and orders can use. A disabled size can't be ordered, and a size without a monthly cost isn't counted in the report.</Note>
        <Button onClick={() => setEdit({ ...FORMS.size.empty })} data-testid="size-add"><Plus />Add a server size</Button>
      </div>
      {data.sizes.length === 0 ? <Empty>No server sizes yet.</Empty> : (
        <Grid cols={SIZE_COLS} min={700}
          head={<><span>Size</span><span>Hetzner type</span><span className="text-right">Sessions at once</span><span className="text-right">Monthly cost</span><span>Enabled</span><span /></>}>
          {data.sizes.map((z) => (
            <Row key={z.id} cols={SIZE_COLS} min={700} data-testid={`size-row-${z.id}`}>
              <span className="text-base font-semibold min-w-0 [overflow-wrap:anywhere]">{z.label}</span>
              <span className="min-w-0">{z.by_hand ? "Installed by hand" : <span className="font-mono">{z.hetzner_type}</span>}
                {z.dedicated && <span className="block text-sm text-muted-foreground">Dedicated vCPUs</span>}</span>
              <span className="text-right">{int(z.max_active_sessions)}</span>
              <span className="text-right">{z.monthly_cost_usd == null ? "—" : usd(z.monthly_cost_usd)}</span>
              <span className="flex"><StatusBadge status={z.enabled ? "on" : "off"} tone={z.enabled ? "info" : "muted"} /></span>
              <EditButton label={`Edit ${z.label}`} onClick={() => setEdit({ ...z, monthly_cost_usd: z.monthly_cost_usd ?? "" })} data-testid={`size-edit-${z.id}`} />
            </Row>
          ))}
        </Grid>
      )}
      <FormDialog kind="size" edit={edit} setEdit={setEdit} saving={save.isPending} onSave={() => save.mutate(edit)} />
    </div>
  );
}

// ---------------- Key pool ----------------
const KEY_TONE = { available: "info", assigned: "plain", revoked: "muted" };
const KEY_COLS = "minmax(0,1.3fr) 90px 90px 120px minmax(0,1fr) 110px 72px";

function KeyPoolTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["store-keys"], queryFn: async () => (await api.get("/admin/store/keys")).data });
  const [form, setForm] = useState({ provider: "anthropic", label: "", key: "" });
  const add = useMutation({
    mutationFn: async () => api.post("/admin/store/keys", { ...form, label: form.label.trim(), key: form.key.trim() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["store-keys"] }); setForm({ ...form, label: "", key: "" }); toast.success("Key added. It won't be shown again."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const [revoking, setRevoking] = useState(null);  // the pool key being revoked
  const revoke = useMutation({
    mutationFn: async ({ id, password }) => api.post(`/admin/store/keys/${id}/revoke`, { password }),
    onSuccess: () => {
      toast.success(revoking?.status === "assigned" ? "Key revoked. Its company gets a new one on its next request." : "Key revoked.");
      setRevoking(null); qc.invalidateQueries({ queryKey: ["store-keys"] });
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!data) return <Loading />;
  const canAdd = !!form.label.trim() && form.key.trim().length >= 8;
  return (
    <div className="flex flex-col gap-4" data-testid="store-keys">
      <ConfirmDestructive open={!!revoking} onOpenChange={(v) => !v && setRevoking(null)} title={`Revoke ${revoking?.label}?`}
        action="Revoke" pending={revoke.isPending} onConfirm={(password) => revoke.mutate({ id: revoking.id, password })}>
        <p>{revoking?.company_name
          ? `Requests with it stop at once and it's never used again. ${revoking.company_name} gets a new key from the pool on its next request. Then delete it in the provider's console.`
          : "It's never used again. Then delete it in the provider's console."}</p>
      </ConfirmDestructive>
      <div className="flex flex-wrap gap-4">
        {PROVIDERS.map(([p, name]) => {
          const n = data.available[p] ?? 0;
          return (
            <div key={p} className="flex-[1_1_240px] bg-card rounded-[10px] px-5 py-4 flex flex-col gap-2">
              <Label11>{name} keys available</Label11>
              <span className="text-[24px] leading-7 tabular-nums" data-testid={`pool-available-${p}`}>{int(n)}</span>
              {n < data.low_at && <span className="text-sm text-gr-warning">Running low: add more before companies switch to credits.</span>}
            </div>
          );
        })}
      </div>
      <Panel title="Add a key">
        <Note>
          One key per company, never shared: an Anthropic workspace key or an OpenAI project key, each made in the provider's console
          with its own monthly spend limit. It's stored encrypted and never shown again.
        </Note>
        <div className="flex flex-wrap gap-2">
          <Segmented options={PROVIDERS} value={form.provider} onChange={(v) => setForm({ ...form, provider: v })} label="Provider" testid="pool-provider" />
          <Input placeholder="Label, e.g. workspace crewbench-c11" value={form.label} aria-label="Label" className="flex-[1_1_200px] w-auto min-w-0"
            onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="pool-label" />
          <Input type="password" autoComplete="off" placeholder="sk-..." value={form.key} aria-label="API key" className="flex-[1.5_1_220px] w-auto min-w-0 font-mono text-[12px]"
            onChange={(e) => setForm({ ...form, key: e.target.value })} data-testid="pool-key" />
          <Button onClick={() => add.mutate()} disabled={!canAdd || add.isPending} data-testid="pool-add">Add</Button>
        </div>
      </Panel>
      {data.keys.length === 0 ? <Empty>No keys in the pool yet.</Empty> : (
        <Grid cols={KEY_COLS} min={820} head={<><span>Label</span><span>Provider</span><span>Key</span><span>Status</span><span>Company</span><span>Assigned</span><span /></>}>
          {data.keys.map((k) => (
            <Row key={k.id} cols={KEY_COLS} min={820} data-testid={`pool-row-${k.id}`}>
              <span className="text-base min-w-0 [overflow-wrap:anywhere]">{k.label}</span>
              <span>{PROVIDER_NAMES[k.provider] || k.provider}</span>
              <span className="font-mono">•••• {k.last4}</span>
              <span className="flex"><StatusBadge status={k.status} tone={KEY_TONE[k.status] || "muted"} /></span>
              <span className="min-w-0 [overflow-wrap:anywhere]">{k.company_name || "—"}</span>
              <span className="text-muted-foreground">{when(k.assigned_at)}</span>
              <span className="flex justify-end">
                {k.status !== "revoked" && <Button size="sm" variant="ghost" onClick={() => setRevoking(k)} data-testid={`pool-revoke-${k.id}`}>Revoke</Button>}
              </span>
            </Row>
          ))}
        </Grid>
      )}
    </div>
  );
}

// ---------------- Settings ----------------
const SETTINGS = [
  ["credit_markup_pct", "Credit markup (%)", "Added to the provider's price for usage on credits. Must cover Lemon Squeezy's fee, fraud and refunds."],
  ["credit_floor_usd", "Credit floor ($)", "New requests stop when a balance is below this."],
  ["low_balance_usd", "Low-balance alert ($)", "Founders are told when their balance drops below this."],
  ["new_company_days", "New company period (days)", "How long a company counts as new for the limits below."],
  ["new_company_topup_max_usd", "New company: top-up limit ($ per purchase)", "Admins can lift it per company."],
  ["new_company_daily_spend_usd", "New company: credit spend limit ($ per day, UTC)", "Admins can lift it per company."],
  ["server_grace_days", "Server grace period (days)", "How long a server is kept after its plan ends, before it can be deleted."],
  // the AI wallet's refunds (backend/wallet.py): 0 and 0 until a real refund's payout shows Lemon Squeezy keeps its fee
  ["refund_fee_pct", "Refund: provider's fee (%)", "Taken off a credits refund while Lemon Squeezy keeps its fee on refunds. 0 for none."],
  ["refund_fee_fixed_usd", "Refund: provider's fee ($ per payment)", "Added per payment refunded. 0 for none."],
  // seats and limits (backend/seats.py)
  ["extra_price_usd", "Extra seat or slot ($ a month)", "The price founders pay for each team member seat or independent professional slot beyond their plan. They add extras themselves, by inviting someone beyond it."],
  ["extra_fee_pct", "Extra: Crewbench's fee (%)", "The rest of an extra's price goes into the AI wallet as credits."],
  // the founder's assistant (backend doc_jobs.due_gate)
  ["debt_limit_usd", "Run on credit, down to ($ below zero)", "A paid plan's scheduled investor update runs even without credits, until the balance would go below minus this. The next credits cover it first."],
];

function SettingsTab() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["store-settings"], queryFn: async () => (await api.get("/admin/store/settings")).data });
  const [form, setForm] = useState({});
  useEffect(() => { if (data) setForm(data.settings); }, [data]);
  const save = useMutation({
    mutationFn: async () => api.put("/admin/store/settings", Object.fromEntries(SETTINGS.map(([k]) => [k, Number(form[k])]))),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["store-settings"] }); toast.success("Store settings saved."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  if (!data) return <Loading />;
  const dirty = SETTINGS.some(([k]) => String(form[k] ?? "") !== String(data.settings[k] ?? ""));
  return (
    <section className="bg-card rounded-[10px] p-5 flex flex-col gap-5" data-testid="store-settings">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-x-8 gap-y-5">
        {SETTINGS.map(([k, label, help]) => (
          <Field key={k} id={`setting-${k}`} label={label}>
            <Input id={`setting-${k}`} type="number" min="0" step="any" value={form[k] ?? ""} className="tabular-nums"
              onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`setting-${k}`} />
            <span className="text-sm text-muted-foreground [text-wrap:pretty]">{help}</span>
          </Field>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending} data-testid="store-settings-save">Save settings</Button>
        {dirty ? <button type="button" onClick={() => setForm(data.settings)} className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground">Discard changes</button>
          : <span className="text-sm text-muted-foreground">No changes to save.</span>}
      </div>
    </section>
  );
}

// Admin → Store: credits and payments (FEATURE_STORE) and workspace servers (FEATURE_PROVISIONING). ?tab= keeps the tab.
const TABS = [
  ["companies", "Companies", CompaniesTab, "store"], ["catalog", "Plans and top-ups", CatalogTab, "store"],
  ["payments", "Payments", PaymentsTab, "store"], ["report", "Report", ReportTab, "store"],
  ["servers", "Servers", ServersTab, "provisioning"], ["sizes", "Server sizes", SizesTab, "either"],
  ["keys", "Key pool", KeyPoolTab, "store"], ["settings", "Settings", SettingsTab, "store"],
];

export default function AdminStore() {
  const flags = useFlags();
  const [params, setParams] = useSearchParams();
  const tabs = TABS.filter(([, , , f]) => (f === "either" ? flags.store || flags.provisioning : flags[f]));
  const tab = tabs.find(([v]) => v === params.get("tab")) || tabs[0];
  // the status line: payments that never reached us and balances the daily check flagged
  const { data: pay } = useQuery({ queryKey: ["store-payments"], enabled: !!flags.store, refetchInterval: 15000,
    queryFn: async () => (await api.get("/admin/store/payments")).data });
  const { data: cos } = useQuery({ queryKey: ["store-companies"], enabled: !!flags.store,
    queryFn: async () => (await api.get("/admin/store/companies")).data });
  const title = `${APP_NAME} credits and workspace servers.`;
  if (!tab) return <Page className="gap-5"><PageHeader eyebrow="Platform · Store" title={title} /><Loading /></Page>;
  let status;
  if (flags.store && pay && cos) {
    const nf = pay.missing.length;
    const nb = new Set(cos.reconciliation.mismatches.map((m) => m.company_id)).size;
    const parts = [nf && plural(nf, "payment"), nb && plural(nb, "balance")].filter(Boolean);
    status = parts.length ? `${parts.join(" and ")} ${nf + nb === 1 ? "needs" : "need"} a look.` : "Nothing needs a look.";
  }
  const [value, , Content] = tab;
  return (
    <Page className="gap-5" data-testid="admin-store">
      <PageHeader eyebrow="Platform · Store" title={title} status={status} />
      <div role="tablist" aria-label="Store" className="self-start max-w-full inline-flex flex-wrap rounded-md border border-gr-control-border overflow-hidden">
        {tabs.map(([v, label]) => (
          <button key={v} type="button" role="tab" aria-selected={v === value} id={`store-tab-${v}`} aria-controls="store-panel"
            onClick={() => setParams(v === tabs[0][0] ? {} : { tab: v }, { replace: true })} data-testid={`store-tab-${v}`}
            className={cn("h-[30px] px-3 font-semibold whitespace-nowrap transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
              v === value ? "bg-gr-selected text-gr-on-selected" : "hover:bg-gr-hover")}>{label}</button>
        ))}
      </div>
      <div role="tabpanel" id="store-panel" aria-labelledby={`store-tab-${value}`}><Content /></div>
    </Page>
  );
}
